import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TravelRecord, TravelSnapshot } from "../../../../domain/travel";
import { useServiceBus } from "../../../hooks/useServiceBus";

interface TravelContextValue {
  travels: TravelRecord[];
  isLoading: boolean;
  hasError: boolean;
  addTravel: (travel: TravelSnapshot) => Promise<boolean>;
  updateTravel: (id: string, travel: TravelSnapshot) => Promise<boolean>;
  deleteTravel: (id: string) => Promise<boolean>;
}

const TravelContext = createContext<TravelContextValue | undefined>(undefined);

export function TravelProvider({ children, healthRecordId }: { children: ReactNode; healthRecordId: string }) {
  const serviceBus = useServiceBus();
  const [travels, setTravels] = useState<TravelRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadTravels = async () => {
      setIsLoading(true);
      setHasError(false);
      const result = await serviceBus.dispatch({ type: "listTravels", healthRecordId });
      if (cancelled) return;

      if (result.type === "success") {
        setTravels(result.value);
      } else {
        setHasError(true);
      }
      setIsLoading(false);
    };

    loadTravels();
    return () => {
      cancelled = true;
    };
  }, [healthRecordId, serviceBus]);

  const addTravel = useCallback(
    async (travel: TravelSnapshot) => {
      const result = await serviceBus.dispatch({ type: "createTravel", healthRecordId, travel });
      if (result.type === "error") return false;

      setTravels((currentTravels) => [...currentTravels, result.value]);
      return true;
    },
    [healthRecordId, serviceBus],
  );

  const updateTravel = useCallback(
    async (id: string, travel: TravelSnapshot) => {
      const result = await serviceBus.dispatch({ type: "updateTravel", healthRecordId, travelId: id, travel });
      if (result.type === "error") return false;

      setTravels((currentTravels) =>
        currentTravels.map((currentTravel) => (currentTravel.id === id ? result.value : currentTravel)),
      );
      return true;
    },
    [healthRecordId, serviceBus],
  );

  const deleteTravel = useCallback(
    async (id: string) => {
      const result = await serviceBus.dispatch({ type: "deleteTravel", healthRecordId, travelId: id });
      if (result.type === "error") return false;

      setTravels((currentTravels) => currentTravels.filter((currentTravel) => currentTravel.id !== id));
      return true;
    },
    [healthRecordId, serviceBus],
  );

  const value = useMemo(
    () => ({ travels, isLoading, hasError, addTravel, updateTravel, deleteTravel }),
    [addTravel, deleteTravel, hasError, isLoading, travels, updateTravel],
  );

  return <TravelContext.Provider value={value}>{children}</TravelContext.Provider>;
}

export function useTravels(): TravelContextValue {
  const context = useContext(TravelContext);
  if (!context) throw new Error("useTravels must be used inside TravelProvider");
  return context;
}
